import { useCallback } from 'react';
import { AccountStatus } from '@common-fe/common-fe';
import * as yup from 'yup';

import {
  LockboxStatus,
  LockboxStatusReason,
  LockboxType,
  LockboxValidationError,
} from '@/modules/employer/components/Lockbox/lockbox.types';
import { EditableLockbox } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';

const inboundStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
  reason: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED || val === LockboxStatus.CANCELED,
    then: () => yup.string().notRequired(),
    otherwise: () => yup.string().required(),
  }),
});

const outboundStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
  reason: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.CANCELED,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(),
  }),
});

const claimRepaymentStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
  reason: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED || val === LockboxStatus.CANCELED,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required(),
  }),
});

const otherTypeStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
});

const genericFormStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
  reason: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.PENDING,
    then: () => yup.string().required(),
    otherwise: () => yup.string().notRequired(),
  }),
});

const returnMailStatusSchema = yup.object().shape({
  lockboxStatus: yup.mixed<LockboxStatus>()
    .transform((value) => (value === LockboxStatus.NEW ? undefined : value))
    .required(),
});

const vendorAddressSchema = {
  then: () =>
    yup.object({
      vendorName: yup.string().required(LockboxValidationError.VENDOR_NAME_REQUIRED),
      addressLine1: yup.string().required(LockboxValidationError.ADDRESS_LINE_1_REQUIRED),
      city: yup.string().required(LockboxValidationError.CITY_REQUIRED),
      state: yup.string().required(LockboxValidationError.STATE_REQUIRED),
      zip: yup.string().required(LockboxValidationError.ZIP_REQUIRED),
    }),
  otherwise: () =>
    yup.object({
      vendorName: yup.string().notRequired(),
      addressLine1: yup.string().notRequired(),
      city: yup.string().notRequired(),
      state: yup.string().notRequired(),
      zip: yup.string().notRequired(),
    }),
};

const inboundFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
  amount: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED || val === LockboxStatus.DECLINED,
    then: (schema) => schema.required(LockboxValidationError.AMOUNT_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  employeeId: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED,
    then: (schema) => schema.required(LockboxValidationError.EMPLOYEE_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  employeeAccountStatus: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED,
    then: (schema) =>
      schema
        .required(LockboxValidationError.HSA_ACCOUNT_REQUIRED)
        .oneOf([AccountStatus.OPEN, AccountStatus.PENDING], LockboxValidationError.HSA_ACCOUNT_CLOSED),
    otherwise: (schema) => schema.notRequired(),
  }),
  notes: yup.string().max(200).notRequired(),
  vendorId: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.DECLINED,
    then: (schema) => schema.required(LockboxValidationError.VENDOR_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  checkDetails: yup.object().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.DECLINED,
    ...vendorAddressSchema,
  }),
});

const outboundFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
  amount: yup.string().when(['lockboxStatus', 'liquidateAllFunds'], {
    is: (lockboxStatus?: LockboxStatus, liquidateAllFunds?: boolean) =>
      lockboxStatus === LockboxStatus.APPROVED && !liquidateAllFunds,
    then: () =>
      yup
        .string()
        .required(LockboxValidationError.AMOUNT_REQUIRED)
        .test('', '', function (amount) {
          const employeeAvailableAmount = this?.parent?.employeeAvailableAmount || 0;

          if (Number(amount) > employeeAvailableAmount) {
            return new yup.ValidationError(LockboxValidationError.AMOUNT_EXCEEDS_AVAILABLE_BALANCE);
          }

          return true;
        }),
    otherwise: (schema) => schema.notRequired(),
  }),
  employeeId: yup.string().when(['lockboxStatus', 'reason'], {
    is: (lockboxStatus?: LockboxStatus, reason?: LockboxStatusReason) =>
      lockboxStatus === LockboxStatus.APPROVED ||
      (lockboxStatus === LockboxStatus.PENDING &&
        reason === LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED),
    then: (schema) => schema.required(LockboxValidationError.EMPLOYEE_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  liquidateAllFunds: yup.boolean().when('employeeInvestmentsBalance', {
    is: (employeeInvestmentsBalance?: number) =>
      !!employeeInvestmentsBalance && employeeInvestmentsBalance > 0,
    then: (schema) => schema.oneOf([false], LockboxValidationError.INVESTMENTS_MUST_BE_LIQUIDATED),
    otherwise: (schema) => schema.notRequired(),
  }),
  employeeAccountStatus: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED,
    then: (schema) =>
      schema.required(LockboxValidationError.HSA_ACCOUNT_REQUIRED).test('', '', (employeeAccountStatus) => {
        if (employeeAccountStatus === AccountStatus.PENDING) {
          return new yup.ValidationError(LockboxValidationError.HSA_ACCOUNT_SHOULD_BE_OPEN);
        }
        if (employeeAccountStatus !== AccountStatus.OPEN) {
          return new yup.ValidationError(LockboxValidationError.HSA_ACCOUNT_CLOSED);
        }
        return true;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  employeeAvailableAmount: yup.number().when(['lockboxStatus', 'liquidateAllFunds'], {
    is: (lockboxStatus?: LockboxStatus, liquidateAllFunds?: boolean) => lockboxStatus === LockboxStatus.APPROVED && liquidateAllFunds,
    then: (schema) =>
      schema.required(LockboxValidationError.AVAILABLE_BALANCE_MUST_BE_GREATER_THAN_ZERO).test('', '', (employeeAvailableAmount) => {
        if (Number(employeeAvailableAmount) <= 0) {
           return new yup.ValidationError(LockboxValidationError.AVAILABLE_BALANCE_MUST_BE_GREATER_THAN_ZERO);
        };
        return true;
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  vendorId: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED,
    then: (schema) => schema.required(LockboxValidationError.VENDOR_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  checkDetails: yup.object().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.APPROVED,
    ...vendorAddressSchema,
  }),
  notes: yup.string().max(200).notRequired(),
});

const claimRepaymentFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
  totalAllocated: yup.string().when(['lockboxStatus', 'claimRepayments'], {
    is: (lockboxStatus?: LockboxStatus, claimRepayments?: unknown[]) => (lockboxStatus === LockboxStatus.APPROVED && claimRepayments && claimRepayments.length !== 0) || lockboxStatus === LockboxStatus.PARTIALLY_APPROVED,
    then: (schema) =>
      schema
        .required(LockboxValidationError.TOTAL_ALLOCATED_EXCEEDED)
        .test(
          LockboxValidationError.TOTAL_ALLOCATED_EXCEEDED,
          LockboxValidationError.TOTAL_ALLOCATED_EXCEEDED,
          function (value) {
            const amount = this.parent.amount;
            if (!value || !amount) return true;
            return Math.round(parseFloat(value) * 100) / 100 <= Math.round(parseFloat(amount) * 100) / 100;
          }
        ),
    otherwise: (schema) => schema.notRequired(),
  }),
  amount: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => [LockboxStatus.APPROVED, LockboxStatus.PARTIALLY_APPROVED, LockboxStatus.DECLINED].includes(val as LockboxStatus),
    then: (schema) => schema.required(LockboxValidationError.AMOUNT_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),

  remainder: yup.object().when(['lockboxStatus', 'reason', 'claimRepayments'], {
    is: (lockboxStatus?: LockboxStatus, reason?: LockboxStatusReason, claimRepayments?: unknown[]) =>
      (lockboxStatus === LockboxStatus.PARTIALLY_APPROVED && reason === LockboxStatusReason.ALLOCATE_REMAINDER_TO_THE_ACCOUNT)
      || (lockboxStatus === LockboxStatus.APPROVED && (!claimRepayments || claimRepayments.length === 0)),
    then: (schema) =>
      schema.shape({
        accountId: yup.string().required(LockboxValidationError.ACCOUNT_REQUIRED),
      }).required(),
    otherwise: (schema) =>
      schema
        .when('lockboxStatus', {
          is: (val?: LockboxStatus) => val === LockboxStatus.DECLINED,
          then: (schema) =>
            schema.shape({
              addressLine1: yup.string().required(LockboxValidationError.CHECK_ADDRESS_REQUIRED),
              payTo: yup.string().required(LockboxValidationError.CHECK_ADDRESS_REQUIRED),
              state: yup.string().required(LockboxValidationError.CHECK_ADDRESS_REQUIRED),
              zip: yup.string().required(LockboxValidationError.CHECK_ADDRESS_REQUIRED),
              city: yup.string().required(LockboxValidationError.CHECK_ADDRESS_REQUIRED),
              benefitOf: yup.string().required(LockboxValidationError.BENEFIT_OF_REQUIRED),
            }).required(),
          otherwise: (schema) => schema.notRequired(),
        })
        .when('reason', {
          is: (val?: LockboxStatusReason) => val === LockboxStatusReason.SEND_THE_REMAINDER_BACK,
          then: (schema) =>
            schema.test(
              LockboxValidationError.REMAINDER_ADDRESS_UNDELIVERABLE,
              LockboxValidationError.REMAINDER_ADDRESS_UNDELIVERABLE,
              (remainder?: { undeliverable?: boolean }) => !remainder?.undeliverable
            ),
          otherwise: (schema) => schema,
        }),
  }),
  claimRepayments: yup.array().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.PENDING,
    then: (schema) =>
      schema
        .of(
          yup.object().shape({
            repaymentAmount: yup
              .number()
              .transform((value, originalValue) => {
                if (originalValue === '') return null;
                const num = Number(originalValue);
                return isNaN(num) ? null : num;
              })
              .required(LockboxValidationError.NEED_TO_ALLOCATE_CLAIM)
              .moreThan(0, LockboxValidationError.NEED_TO_ALLOCATE_CLAIM),
          })
        ).notRequired(),
    otherwise: (schema) =>
      schema.when('lockboxStatus', {
        is: (val?: LockboxStatus) => [LockboxStatus.APPROVED, LockboxStatus.PARTIALLY_APPROVED].includes(val as LockboxStatus),
        then: (schema) =>
          schema
            .of(
              yup.object().shape({
                employeeId: yup.string().required(),
                repaymentAmount: yup
                  .number()
                  .transform((value, originalValue) => {
                    if (originalValue === '') return null;
                    const num = Number(originalValue);
                    return isNaN(num) ? null : num;
                  })
                  .required(LockboxValidationError.NEED_TO_ALLOCATE_CLAIM)
                  .moreThan(0, LockboxValidationError.NEED_TO_ALLOCATE_CLAIM)
                  .test(
                    LockboxValidationError.TOO_MUCH_ALLOCATED,
                    LockboxValidationError.TOO_MUCH_ALLOCATED,
                    function (value) {
                      const amount = this.parent.claimAmount || 0;
                      if (!value) return true;
                      return value <= parseFloat(amount);
                    }
                  ),
              })
            )
            .test(
              LockboxValidationError.CLAIMS_MUST_HAVE_THE_SAME_EMPLOYEE,
              LockboxValidationError.CLAIMS_MUST_HAVE_THE_SAME_EMPLOYEE,
              (claims) => {
                if (!claims || claims.length === 0) return true;
                const firstEmployeeId = claims[0].employeeId;
                return claims.every((item) => item.employeeId === firstEmployeeId);
              }
            )
            .when('lockboxStatus', {
              is: LockboxStatus.APPROVED,
              then: (schema) =>
                schema.test(
                  'skip-validation-if-no-claims',
                  LockboxValidationError.CLAIM_REQUIRED,
                  (claims) => {
                    if (!claims || claims.length === 0) return true;
                    return claims.length >= 1;
                  }
                ),
              otherwise: (schema) =>
                schema
                  .required(LockboxValidationError.CLAIM_REQUIRED)
                  .min(1, LockboxValidationError.CLAIM_REQUIRED),
            }),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  employeeId: yup.string().when(['lockboxStatus', 'claimRepayments'], {
    is: (lockboxStatus?: LockboxStatus, claimRepayments?: unknown[]) =>
      lockboxStatus === LockboxStatus.APPROVED && (!claimRepayments || claimRepayments.length === 0),
    then: (schema) => schema.required(LockboxValidationError.EMPLOYEE_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const otherTypeFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
});

const genericFormFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
  employeeId: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.PROCESSED_MANUALLY,
    then: (schema) => schema.required(LockboxValidationError.EMPLOYEE_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
  formName: yup.string().when(['lockboxType', 'lockboxStatus'], {
    is: (lockboxType?: LockboxType, lockboxStatus?: LockboxStatus) =>
      lockboxType === LockboxType.HSA_FORM && lockboxStatus === LockboxStatus.PROCESSED_MANUALLY,
    then: (schema) => schema.required(LockboxValidationError.FORM_NAME_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const returnMailFieldsSchema = yup.object().shape({
  lockboxType: yup.mixed<LockboxType>().required(LockboxValidationError.TYPE_REQUIRED),
  employeeId: yup.string().when('lockboxStatus', {
    is: (val?: LockboxStatus) => val === LockboxStatus.PROCESSED_MANUALLY,
    then: (schema) => schema.required(LockboxValidationError.EMPLOYEE_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  }),
});

const useValidateLockbox = () => {
  // @ts-ignore
  const validateLockbox = useCallback((lockboxData: EditableLockbox, schema: yup.ObjectSchema) => {
    try {
      schema.validateSync(lockboxData, { abortEarly: false });
      return [] as LockboxValidationError[];
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        return error.errors as LockboxValidationError[];
      }
      return [] as LockboxValidationError[];
    }
  }, []);

  const validateLockboxStatus = useCallback(
    (editableLockbox?: EditableLockbox): LockboxValidationError[] => {
      switch (editableLockbox?.lockboxType) {
        case LockboxType.INBOUND_TOA:
          return validateLockbox(editableLockbox, inboundStatusSchema);
        case LockboxType.OUTBOUND_TOA:
          return validateLockbox(editableLockbox, outboundStatusSchema);
        case LockboxType.CLAIM_REPAYMENT:
          return validateLockbox(editableLockbox, claimRepaymentStatusSchema);
        case LockboxType.BENEFICIARY:
          return validateLockbox(editableLockbox, otherTypeStatusSchema);
        case LockboxType.HSA_FORM:
        case LockboxType.EXCESS_CONTRIBUTION:
        case LockboxType.COMBINE_HSA:
        case LockboxType.MISTAKEN_DISTRIBUTION:
        case LockboxType.HSA_ROLLOVER_FORM:
        case LockboxType.HSA_CONTRIBUTION_FORM:
        case LockboxType.HSA_TRANSFER_DIVORCE:
        case LockboxType.HSA_ACCOUNT_CLOSURE:
        case LockboxType.DEATH_DISTRIBUTION:
        case LockboxType.POWER_OF_ATTORNEY:
        case LockboxType.RETURNED_CHECK:
          return validateLockbox(
            editableLockbox,
            genericFormStatusSchema,
          );
        case LockboxType.RETURNED_MAIL:
          return validateLockbox(
            editableLockbox,
            returnMailStatusSchema,
          );
        default:
          return [LockboxValidationError.TYPE_REQUIRED];
      }
    },
    [validateLockbox]
  );

  const validateLockboxFields = useCallback(
    (editableLockbox?: EditableLockbox): LockboxValidationError[] => {
      switch (editableLockbox?.lockboxType) {
        case LockboxType.INBOUND_TOA:
          return validateLockbox(editableLockbox, inboundFieldsSchema);
        case LockboxType.OUTBOUND_TOA:
          return validateLockbox(editableLockbox, outboundFieldsSchema);
        case LockboxType.CLAIM_REPAYMENT:
          return validateLockbox(editableLockbox, claimRepaymentFieldsSchema);
        case LockboxType.BENEFICIARY:
          return validateLockbox(editableLockbox, otherTypeFieldsSchema);
        case LockboxType.HSA_FORM:
        case LockboxType.EXCESS_CONTRIBUTION:
        case LockboxType.COMBINE_HSA:
        case LockboxType.MISTAKEN_DISTRIBUTION:
        case LockboxType.HSA_ROLLOVER_FORM:
        case LockboxType.HSA_CONTRIBUTION_FORM:
        case LockboxType.HSA_TRANSFER_DIVORCE:
        case LockboxType.HSA_ACCOUNT_CLOSURE:
        case LockboxType.DEATH_DISTRIBUTION:
        case LockboxType.POWER_OF_ATTORNEY:
        case LockboxType.RETURNED_CHECK:
          return validateLockbox(
            editableLockbox,
            genericFormFieldsSchema,
          );
        case LockboxType.RETURNED_MAIL:
          return validateLockbox(
            editableLockbox,
            returnMailFieldsSchema,
          );
        default:
          return [LockboxValidationError.TYPE_REQUIRED];
      }
    },
    [validateLockbox]
  );

  return {
    validateLockboxStatus,
    validateLockboxFields,
  };
};

export default useValidateLockbox;
